/**
 * Algorithm Tab Component - JavaScript Version
 * 
 * Handles algorithm selection, execution, and path visualization.
 * Extracted from the main ShortestPathPage.js file.
 */

import React, { useState, useEffect } from 'react';
import { getDefaultGraph, getSampleGraphs } from '../../data/sampleGraphs';
import { getAvailableAlgorithms as getBackendAlgorithms, getSampleGraphs as getBackendSampleGraphs } from '../../services/APIService';
import AlgorithmSelector from './AlgorithmSelector';
import PathDisplay from './PathDisplay';

const AlgorithmTab = ({
  graphData,
  setGraphData,
  selectedPath,
  setSelectedPath,
  startNode,
  setStartNode,
  endNode,
  setEndNode,
  onNodeClick,
  stepData,
  nodes,
  // Shared algorithm state
  result,
  isLoading,
  error,
  currentStep,
  totalSteps,
  runAlgorithm,
  nextStep,
  previousStep,
  goToStep,
  getCurrentStep,
  reset
}) => {
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('dijkstra');
  const [algorithms, setAlgorithms] = useState([]);
  const [backendGraphs, setBackendGraphs] = useState({});
  const [useBackend, setUseBackend] = useState(true);

  // Load algorithms and sample graphs from backend on component mount
  useEffect(() => {
    const loadBackendData = async () => {
      try {
        // Try to load from backend first with timeout
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 5000)
        );
        
        const backendPromise = Promise.all([
          getBackendAlgorithms(),
          getBackendSampleGraphs()
        ]);
        
        const [backendAlgorithms, backendSampleGraphs] = await Promise.race([
          backendPromise,
          timeoutPromise
        ]);
        
        // Add isAvailable property to backend algorithms
        const algorithmsWithAvailability = backendAlgorithms.map(alg => ({
          ...alg,
          isAvailable: true
        }));
        setAlgorithms(algorithmsWithAvailability);
        setBackendGraphs(backendSampleGraphs);
        setUseBackend(true);
      } catch (error) {
        console.warn('Backend not available, using frontend fallback:', error.message);
        // Fallback to frontend algorithms - create mock algorithm options
        const mockAlgorithms = [
          {
            id: 'dijkstra',
            name: "Dijkstra's Algorithm",
            description: 'Finds shortest path in weighted graphs with non-negative weights',
            complexity: 'O((V + E) log V)',
            suitableFor: ['Positive weights', 'Single source', 'Optimal path'],
            isAvailable: true
          }
        ];
        setAlgorithms(mockAlgorithms);
        setUseBackend(false);
      }
    };

    loadBackendData();
  }, []);

  const handleNodeClick = (node) => {
    if (!startNode) {
      setStartNode(node);
      setSelectedPath([node.id]);
    } else if (!endNode && node.id !== startNode.id) {
      setEndNode(node);
      setSelectedPath([startNode.id, node.id]);
    } else {
      // Reset selection
      setStartNode(null);
      setEndNode(null);
      setSelectedPath([]);
    }
  };

  const loadSampleGraph = (graphKey) => {
    // Always use frontend graphs for complex graph to ensure updated version
    const graphs = (graphKey === 'complex') ? getSampleGraphs() : (useBackend ? backendGraphs : getSampleGraphs());
    const graphData = graphs[graphKey];
    
    if (graphData) {
      setGraphData(graphData);
      setStartNode(null);
      setEndNode(null);
      setSelectedPath([]);
      reset();
    }
  };

  const handleCalculatePath = async () => {
    if (!startNode || !endNode) return;
    
    try {
      // Backend expects edges with from_ property (Canvas format)
      // Ensure all edges have the required properties for the API
      const apiEdges = graphData.edges.map(edge => ({
        from_: parseInt(edge.from_.toString()),
        to: parseInt(edge.to.toString()),
        length: parseInt(edge.length.toString()),
        label: edge.label || `${edge.from_}-${edge.to}`
      }));
      
      // Ensure node IDs are integers
      const apiNodes = graphData.nodes.map(node => ({
        id: parseInt(node.id.toString()),
        label: node.label,
        x: parseInt(node.x.toString()),
        y: parseInt(node.y.toString()),
        fixed: Boolean(node.fixed)
      }));
      
      console.log('Calling runAlgorithm with:', {
        algorithm: selectedAlgorithm,
        nodes: apiNodes,
        edges: apiEdges,
        start: parseInt(startNode.id.toString()),
        end: parseInt(endNode.id.toString())
      });
      
      const result = await runAlgorithm(
        selectedAlgorithm,
        apiNodes,
        apiEdges,
        parseInt(startNode.id.toString()),
        parseInt(endNode.id.toString())
      );
      
      console.log('Algorithm result:', result);
      console.log('Result steps:', result.steps);
      if (result.steps && result.steps.length > 0) {
        console.log('First step:', result.steps[0]);
      }
      
      if (result.success) {
        setSelectedPath(result.path);
        console.log('Path found:', result.path);
      } else {
        console.log('No path found or algorithm failed');
        setSelectedPath([]);
      }
    } catch (err) {
      console.error('Algorithm execution failed:', err);
      alert('Algorithm execution failed. Please try again.');
    }
  };

  const handleStepChange = (stepIndex) => {
    goToStep(stepIndex);
  };

  const getCurrentStepData = () => {
    return getCurrentStep();
  };

  return (
    <div className="algorithm-tab">
      <div className="control-panel">
        <h3>Algorithm Selection</h3>
        <AlgorithmSelector
          algorithms={algorithms}
          selectedAlgorithm={selectedAlgorithm}
          onAlgorithmChange={setSelectedAlgorithm}
          isLoading={isLoading}
        />

        <div className="graph-controls">
          <h3>Sample Graphs</h3>
          <div className="graph-selector">
            <button 
              className="graph-btn"
              onClick={() => loadSampleGraph('simple')}
            >
              Simple Graph
            </button>
            <button 
              className="graph-btn"
              onClick={() => loadSampleGraph('complex')}
            >
              Complex Graph
            </button>
          </div>
        </div>

        <div className="path-info">
          <h3>Path Selection</h3>
          <p>Click two nodes to select start and end points</p>
          {startNode && (
            <p><strong>Start:</strong> {startNode.label}</p>
          )}
          {endNode && (
            <p><strong>End:</strong> {endNode.label}</p>
          )}
          {startNode && endNode && (
            <div className="execution-controls">
              <button 
                className="calculate-btn"
                onClick={handleCalculatePath}
                disabled={isLoading}
              >
                {isLoading ? 'Calculating...' : 'Calculate Shortest Path'}
              </button>
              
              {result && result.steps && result.steps.length > 0 && (
                <div className="step-controls">
                  <button 
                    className="step-btn"
                    onClick={() => goToStep(0)}
                    disabled={currentStep === 0}
                  >
                    ⏮️ First
                  </button>
                  <button 
                    className="step-btn"
                    onClick={previousStep}
                    disabled={currentStep === 0}
                  >
                    ⏪ Previous
                  </button>
                  <button 
                    className="step-btn"
                    onClick={nextStep}
                    disabled={currentStep >= totalSteps - 1}
                  >
                    Next ⏩
                  </button>
                  <button 
                    className="step-btn"
                    onClick={() => goToStep(totalSteps - 1)}
                    disabled={currentStep >= totalSteps - 1}
                  >
                    Last ⏭️
                  </button>
                </div>
              )}
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default AlgorithmTab;
